import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { APIS } from '@constants/apis'
import useFetch from '@core/hooks/useFetch'

const EmployeeProfileContext = createContext(null)

export const useEmployeeProfile = () => useContext(EmployeeProfileContext)

const ProfileScope = ({ userData, children }) => {
  const employeeCode = userData?._pis?.employeeCode
  const pictureKey = userData?.profilePicture
  const currentPicture = useRef(pictureKey)
  currentPicture.current = pictureKey
  const [profile, setProfile] = useState(null)
  const [profileStatus, setProfileStatus] = useState('idle')
  const [picture, setPicture] = useState(null)
  const { execute: getUserInfo } = useFetch(APIS.USER.INFO)
  const { execute: getProfileImage } = useFetch(
    APIS.FILES.GET_PROFILE_PICTURE_DOWNLOAD_URL
  )

  useEffect(() => {
    if (!employeeCode) return
    let active = true
    setProfileStatus('loading')
    getUserInfo(
      { url: employeeCode },
      {
        onSuccess: ({ data }) => {
          if (!active) return
          setProfile(data || null)
          setProfileStatus(data ? 'success' : 'error')
        },
        onFailed: () => {
          if (active) setProfileStatus('error')
        }
      }
    )
    return () => {
      active = false
    }
  }, [employeeCode, getUserInfo])

  useEffect(() => {
    if (!employeeCode || !pictureKey) return
    let active = true
    const isCurrent = () => active && currentPicture.current === pictureKey
    setPicture({ key: pictureKey, status: 'loading' })
    getProfileImage(
      { payload: { profilePicture: pictureKey, isTemp: false } },
      {
        onSuccess: ({ data }) => {
          if (!isCurrent()) return
          setPicture({
            key: pictureKey,
            url: data?.url || undefined,
            status: data?.url ? 'success' : 'error'
          })
        },
        onFailed: () => {
          if (isCurrent()) setPicture({ key: pictureKey, status: 'error' })
        }
      }
    )
    return () => {
      active = false
    }
  }, [employeeCode, pictureKey, getProfileImage])

  const activePicture =
    pictureKey && picture?.key === pictureKey ? picture : null
  const user = profile
    ? {
        firstName: profile.pisFirstName,
        lastName: profile.pisLastName,
        firstNameEN: profile.pisFirstNameEN,
        lastNameEN: profile.pisLastNameEN,
        jobPositionName: profile.pisJobPositionName,
        jobPositionNameEN: profile.pisJobPositionNameEN,
        email: profile.pisEmail,
        employeeCode,
        groupModuleList: userData?._pis?.groupModuleList,
        isSet: true
      }
    : { isSet: false }

  return (
    <EmployeeProfileContext.Provider
      value={{
        user,
        image: activePicture?.url,
        profileStatus,
        imageStatus: activePicture?.status || 'idle'
      }}
    >
      {children}
    </EmployeeProfileContext.Provider>
  )
}

export const EmployeeProfileProvider = ({ userData, children }) => {
  // A new identity gets a fresh scope; no profile or signed URL survives it.
  const scope = JSON.stringify([
    userData?._pis?.companyGroupCode,
    String(userData?.companyId ?? ''),
    userData?._pis?.employeeCode
  ])

  return (
    <ProfileScope key={scope} userData={userData}>
      {children}
    </ProfileScope>
  )
}

export default EmployeeProfileProvider
